<template>
    <div class="dequeue__info">
        <div class="dequeue__item-avatar">
            <img class="img-thumbnail" :src="getAvatar()">
        </div>
        <div class="dequeue__item-description">
            <span class="dequeue__item-number float-right">
                {{ user.number }}
            </span>
            <div class="dequeue__item-name">
                {{ user.name }}
                <span v-if="user.queueType && user.queueType == 'dequeued'" class="icon__success" data-toggle="tooltip"
                    data-placement="bottom" :title="user.queueReason">
                    <i class="far fa-check-square"></i>
                </span>
                <span v-else-if="user.queueType && user.queueType == 'removed'" class="icon__warning"
                    data-toggle="tooltip" data-placement="bottom" :title="user.queueReason">
                    <i class="far fa-times-square"></i>
                </span>
            </div>
            <div class="dequeue__item-contact">
                <b>Student Number:</b> {{ user.emplid }}, <b>Western Email:</b> {{ user.emailid }}
            </div>
            <div class="dequeue__item-checkin">
                <b>Check-in:</b> {{ getLocalTime }}
            </div>
            <div
                :class="['dequeue__item-appointmenttype', !user.appointmentType ? 'dequeue__item-appointmenttype--unassigned' : 'dequeue__item-appointmenttype--assigned']">
                <b>Appointment Type:</b> {{ getAppointmentType }}
            </div>
            <div
                :class="['dequeue__item-counsellor', !user.counsellor ? 'dequeue__item-counsellor--unassigned' : 'dequeue__item-counsellor--assigned']">
                <b>Counsellor:</b> {{ getCounsellor }}
            </div>
            <div v-if="user.queueType && user.queueType == 'removed'" class="dequeue__item-removed">
                <b>Removed Reason:</b> {{ user.queueReason }}
            </div>
            <div
                :class="['dequeue__item-waitingarea', !user.waitingArea ? 'dequeue__item-waitingarea--unassigned' : 'dequeue__item-waitingarea--assigned']">
                <b>Waiting Area:</b> {{ getWaitingArea }}
            </div>
            <div class="dequeue__item-action">
                <span v-if="!user.cancellationReason" class="btn-tooltip mr-2" data-toggle="tooltip" data-placement="bottom" title="Requeue">
                    <button type="button" class="btn btn-icon btn-sm btn-sm btn-light-warning" data-toggle="modal"
                        :data-target="'#' + getDequeueId" @click="getAssignedCounsellor()">
                        <i class="far fa-history"></i>
                    </button>
                </span>
                <span v-if="user.cancellationReason" class="btn-tooltip mr-2" data-toggle="tooltip"
                    data-placement="bottom" title="View Cancellation Reason">
                    <button type="button" class="btn btn-icon btn-sm btn-sm btn-light-danger" data-toggle="modal"
                        :data-target="'#' + getCancellationId">
                        <i class="fas fa-ban"></i>
                    </button>
                </span>
                <span v-if="user.triageData" class="btn-tooltip" data-toggle="tooltip" data-placement="bottom"
                    title="View Triage Information">
                    <button type="button" class="btn btn-icon btn-sm btn-light-primary" data-toggle="modal"
                        :data-target="'#' + getViewId">
                        <i class="fas fa-file-alt"></i>
                    </button>
                </span>
            </div>
        </div>
        <!-- Modal -->
        <!-- Dequeue -->
        <div class="modal fade" :id="getDequeueId" tabindex="-1" role="dialog" aria-labelledby="requeueItem"
            aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <div class="modal-title" id="requeueItem">Requeue Item</div>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Cancel">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <i class="far fa-exclamation-triangle fa-2x icon__warning"></i>
                        <span v-if="user.name">
                            You are about to <b>requeue</b> the item of <b>{{ user.name }}</b>. Please click the
                            <b>confirm</b>
                            button to
                            proceed.
                        </span>
                        <span v-else>
                            You are about to <b>requeue</b> the item. Please click the <b>confirm</b> button to
                            proceed.
                        </span>
                        <form :id="getFormDequeueId">
                            <div class="form-group mt-2 mb-0">
                                <label class="mb-0" for="assignNewNumber">Select a requeue option:</label>
                                <select class="form-control form-control-sm" id="assignNewNumber"
                                    name="assignNewNumber">
                                    <option value="0">Place in queue with original number</option>
                                    <option value="1">Assign a new number and place at the end</option>
                                </select>
                            </div>
                            <div class="form-group mt-2 mb-0">
                                <label class="mb-0" for="counsellor">Assigned Counsellor:</label>
                                <select class="form-control form-control-sm" id="counsellor" name="counsellor"
                                    v-model="assignedCounsellor">
                                    <option value="">None</option>
                                    <option v-for="counsellor in counsellors" :key="counsellor.username"
                                        :value="counsellor.username"> {{ counsellor.commonName }}</option>
                                </select>
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-sm btn-primary" data-dismiss="modal"
                            @click="addQueueItem">Confirm</button>
                        <button type="button" class="btn btn-sm btn-warning" data-dismiss="modal">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
        <!-- Cancellation information -->
        <div v-if="user.cancellationReason" class="modal fade" :id="getCancellationId" tabindex="-1" role="dialog"
            aria-labelledby="itemCancellationInformation" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <div class="modal-title" id="itemCancellationInformation">Cancellation Information</div>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Cancel">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <span>
                            <h6 class="font-weight-bold">Reason</h6>
                            <p>{{ user.cancellationReason }}</p>
                        </span>
                        <span v-if="user.cancellationReason == 'Other'">
                            <h6 class="font-weight-bold">Description</h6>
                            <p>{{ user.cancellationReasonOther }}</p>
                        </span>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-sm btn-warning" data-dismiss="modal">Close</button>
                    </div>
                </div>
            </div>
        </div>
        <!-- Triage form -->
        <div v-if="user.triageData" class="modal fade" :id="getViewId" tabindex="-1" role="dialog"
            aria-labelledby="itemTriageInformation" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-dialog-large" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <div class="modal-title" id="itemTriageInformation">Triage Information</div>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Cancel">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <triage-form-preview :triageData="user.triageData"></triage-form-preview>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-sm btn-warning" data-dismiss="modal">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import $ from 'jquery';

const DEPARTMENT = `Academic Dean's Office`;
import TriageFormPreview from '../Triage/TriageForm.vue';

export default {
    name: 'dequeue-item',
    components: {
        TriageFormPreview
    },
    props: {
        counsellors: {
            type: Array,
            required: true
        },
        user: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            assignedCounsellor: ''
        }
    },
    methods: {
        addQueueItem() {
            const that = this;
            const api = `https://${location.hostname}/cgi-bin/mkvn-cardservices-backend/controller.cardservices.pl`;
            const formRef = document.getElementById(`requeue${this.user.itemid}`);
            const formData = new FormData(formRef);

            // Add form keypair values
            formData.append('id', this.user.itemid);
            formData.append('mode', 'requeueItem');
            formData.append('department', DEPARTMENT);
            formData.append('queueType', 'queued');
            formData.append('queueReason', 'Requeued');

            // POST request
            fetch(api, {
                method: 'POST',
                body: formData
            }).then(function (response) {
                return response.json();
            }).then(function (json) {
                // Success
                if (json.result.match(/^OK/i)) {

                } else {
                    // Fail
                    that.$emit('setNotificationMessage', {
                        message: `The queue item for ${that.user.name} could not be requeued. Please contact your system administrator.`,
                        type: "warning"
                    });
                }

                // Refresh queue
                that.$emit('refreshQueue');
            }).catch(function (error) {
                // Fail
                that.$emit('setNotificationMessage', {
                    message: `The queue item for ${that.user.name} could not be requeued. Please contact your system administrator.`,
                    type: "warning"
                });

                // Refresh queue
                that.$emit('refreshQueue');
            })
        },
        getAssignedCounsellor() {
            this.assignedCounsellor = this.user.counsellor || '';
        },
        getAvatar() {
            if (this.user && this.user.photo) {
                return `data:image/jpeg;base64,` + btoa(`${this.user.photo}`);
            }

            return `../src/assets/avatar/profile.jpg`;
        }
    },
    computed: {
        getAppointmentType() {
            if (this.user.appointmentType && this.user.appointmentType == "inperson") {
                return 'In-person';
            }

            if (this.user.appointmentType && this.user.appointmentType == "virtual") {
                return 'Virtual';
            }

            return 'Not specified';
        },
        getCancellationId() {
            return `cancel${this.user.itemid}`;
        },
        getCounsellor() {
            const assignedCounsellor = this.user.counsellor;
            let counsellor;

            // Find counsellor
            if (assignedCounsellor) {
                counsellor = this.counsellors.find(function (counsellor) {
                    return counsellor.username.match(assignedCounsellor);
                });

                if (counsellor && counsellor.commonName) {
                    return counsellor.commonName;
                }
            }

            return 'Not assigned';
        },
        getDequeueId() {
            return `dequeue${this.user.itemid}`;
        },
        getFormDequeueId() {
            return `requeue${this.user.itemid}`;
        },
        getLocalTime() {
            let timestamp = this.user.timestampCheckin * 1000;
            let date = new Date(timestamp);

            // Return local date/time
            return date.toLocaleString();
        },
        getViewId() {
            return `view${this.user.itemid}`;
        },
        getWaitingArea() {
            return this.user.waitingArea || 'Not assigned';
        }
    },
    mounted() {
        // Tooltip inialization
        $('[data-toggle="tooltip"]').tooltip({
            trigger: 'hover'
        })

        $('[data-toggle="tooltip"]').on('click', function () {
            $(this).tooltip('hide')
        })
    }
}
</script>

<style scoped>
.icon__success {
    color: #0abb87;
}

.icon__warning {
    color: #ffb822;
}

.modal-body {
    font-size: 14px;
}

.modal-dialog-large {
    max-width: 992px !important;
}

.modal-title {
    font-weight: 600;
    font-size: 1rem;
    color: #48465b;
}

.dequeue__item-action {
    margin-top: .5rem;
}

.dequeue__item-appointmenttype {
    font-size: 14px;
    margin-top: .25rem;
}

.dequeue__item-appointmenttype--assigned {
    color: #0abb87;
}

.dequeue__item-appointmenttype--unassigned {
    color: #ffb822;
}

.dequeue__item-avatar {
    float: left;
    padding-right: 1rem;
    padding-top: 6px;
}

.dequeue__item-avatar img {
    object-fit: cover;
    width: 96px;
    height: 96px;
}

.dequeue__item-checkin,
.dequeue__item-contact {
    font-size: 14px;
    margin: 0;
    color: #74788d;
}

.dequeue__item-counsellor {
    font-size: 14px;
}

.dequeue__item-counsellor--assigned {
    color: #5578eb;
}

.dequeue__item-counsellor--unassigned {
    color: #ffb822;
}

.dequeue__item-description {
    padding-left: 112px;
}

.dequeue__item-name {
    font-weight: 700;
    font-size: 16px;
    color: #48465b;
    -webkit-transition: color 0.3s ease;
    transition: color 0.3s ease;
}

.dequeue__item-number {
    color: #fd397a;
    font-size: 1.25rem;
    font-weight: 600;
}

.dequeue__item-removed {
    color: #fd397a;
    font-size: 14px;
}

.dequeue__item-waitingarea {
    font-size: 14px;
}

.dequeue__item-waitingarea--assigned {
    color: #fd397a;
}

.dequeue__item-waitingarea--unassigned {
    color: #ffb822;
}
</style>